"use client"

import { useState, useRef, useEffect } from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  GripVertical,
  Star,
  MoreVertical,
  Edit,
  Trash2,
  Award,
  FlameIcon as Fire,
  PackageSearch
} from "lucide-react"
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable"
import { useCloud } from "../hooks/useCloud"
import WarningDialog from "./WarningDialog"
import Switch from '@mui/material/Switch';

// Original ProductCard component
export function ProductCard({ product, onEdit, onDelete, toggleStock }) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showDeleteWarning, setShowDeleteWarning] = useState(false)
  const dropdownRef = useRef(null)

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: product?.id?.toString() })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const renderStars = (rating) => {
    const ratingNum = Number.parseFloat(rating)
    const stars = []
    const fullStars = Math.floor(ratingNum)
    const hasHalfStar = ratingNum % 1 >= 0.5

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="h-3 w-3 fill-yellow-400 text-yellow-400" />)
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative">
          <Star className="h-3 w-3 text-yellow-400" />
          <div className="absolute inset-0 overflow-hidden w-[50%]">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
          </div>
        </div>,
      )
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-3 w-3 text-yellow-400" />)
    }

    return stars
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex flex-col rounded-md border border-gray-300 bg-white p-3"
    >
      <WarningDialog
        title={"please confirm to delete ?"}
        isOpen={showDeleteWarning}
        onCancel={() => setShowDeleteWarning(false)}
        onConfirm={() => onDelete(product.id)}
      />
      {/* Header with name, veg indicator */}
      <div className="flex items-center mb-2">
        <div {...attributes} {...listeners} className="mr-2 cursor-grab active:cursor-grabbing">
          <GripVertical className="h-4 w-4 text-gray-500" />
        </div>
        <div className="flex items-center">
          <h3 className="font-semibold text-sm">{product.itemName}</h3>
          {product.itemType.veg && (
            <span className="ml-2 flex h-4 w-4 items-center justify-center rounded-sm border border-green-500">
              <span className="h-2 w-2 rounded-full bg-green-500"></span>
            </span>
          )}
          {product.itemType.nonVeg && (
            <span className="ml-2 flex h-4 w-4 items-center justify-center rounded-sm border border-red-500">
              <span className="h-2 w-2 rounded-full bg-red-500"></span>
            </span>
          )}
        </div>

        <div ref={dropdownRef} className="ml-auto">
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-gray-100"
            >
              <MoreVertical className="h-4 w-4" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 w-36 rounded-md border border-gray-300 bg-white shadow-md">
                <div className="py-1">
                  <button
                    onClick={() => {
                      onEdit(product.id)
                      setDropdownOpen(false)
                    }}
                    className="flex w-full items-center px-4 py-2 text-xs hover:bg-gray-100"
                  >
                    <Edit className="mr-2 h-3 w-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteWarning(true)
                      setDropdownOpen(false)
                    }}
                    className="flex w-full items-center px-4 py-2 text-xs text-red-600 hover:bg-gray-100"
                  >
                    <Trash2 className="mr-2 h-3 w-3" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-1 mb-2">
        <span
          className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs cursor-pointer font-semibold ${product.status === 'active'
            ? "bg-green-100 text-green-800 border border-green-200"
            : "bg-red-100 text-red-800 border border-red-200"
            }`}
        >
          {product.status === 'active' ? "In Stock" : "Out of Stock"}
          <Switch checked={product.status === 'active'} onChange={() => toggleStock(product)} size="small" />
        </span>

        {product.itemType.chefSpecial && (
          <span className="inline-flex items-center rounded-full bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-800">
            <Award className="mr-0.5 h-3 w-3" />
            Chef's Special
          </span>
        )}

        {product.itemType.bestSeller && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
            <Fire className="mr-0.5 h-3 w-3" />
            Best Seller
          </span>
        )}
      </div>

      {/* Main content: Image and prices */}
      <div className="flex space-x-3">
        {/* Image */}
        <div className="relative h-14 w-14 overflow-hidden rounded-md border border-gray-300 flex-shrink-0">
          {product.images.length > 0  ? (
            <img src={product.images[0]} alt={product.itemName} className="h-full w-full object-cover" crossOrigin="anonymous" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-100">
              <span className="text-xs text-gray-500">No image</span>
            </div>
          )}
        </div>

        {/* Prices and ratings */}
        <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1 text-end">
          <div>
            <p className="text-xs text-gray-500">Price</p>
            {product.discountAmount > 0 ? (
              <div>
                <p className="text-xs line-through text-gray-400">₹{product.price}</p>
                <p className="text-sm font-semibold ">₹{product.price - product.discountAmount}</p>
              </div>
            ) : (
              <p className="text-sm font-semibold">₹{product.price}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500">Ratings</p>
            <div className="flex justify-end">
              {renderStars(product.rating)}
              <span className="ml-1 text-xs">({product.rating})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Description and category at bottom */}
      <div className="mt-2 pt-2 border-t border-gray-300">
        <div className="flex justify-between items-center mb-1">
          <p className="text-xs text-gray-500">
            Category: <span className="text-gray-900">{product.category}</span>
          </p>
          {product.subCategoryName !== "Na" && (
            <p className="text-xs text-gray-500">
              Sub: <span className="text-gray-900">{product.subCategoryName}</span>
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500 line-clamp-1">{product.description || "No description"}</p>
      </div>
    </div>
  )
}

// New ProductTable component for tabular view
export function ProductTable({ products, onEdit, onDelete, toggleStock }) {
  const renderStars = (rating) => {
    const ratingNum = Number.parseFloat(rating)
    const stars = []
    const fullStars = Math.floor(ratingNum)
    const hasHalfStar = ratingNum % 1 >= 0.5

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="h-3 w-3 fill-yellow-400 text-yellow-400" />)
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative">
          <Star className="h-3 w-3 text-yellow-400" />
          <div className="absolute inset-0 overflow-hidden w-[50%]">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
          </div>
        </div>,
      )
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-3 w-3 text-yellow-400" />)
    }

    return stars
  }

  return (
    <div className="overflow-x-auto border border-gray-300 rounded-md">
      <table className="w-full border-collapse">
        {products.length > 0 ?
          <>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-300">
                <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-4 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-4 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                <th className="px-4 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <ProductTableRow key={product.id} product={product} onEdit={onEdit} onDelete={onDelete} toggleStock={toggleStock} />
              ))}
            </tbody>
          </>
          :
          <div className="flex w-full justify-center items-center py-20 ">
            <div className="items-center ">
              <PackageSearch className="h-10 w-10 m-auto stroke-gray-400" />
              <p className="text-gray-400">No Products added </p>
            </div>
          </div>
        }
      </table>
    </div>
  )
}

function ProductTableRow({ product, onEdit, onDelete, toggleStock  }) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showDeleteWarning, setShowDeleteWarning] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const renderStars = (rating) => {
    const ratingNum = Number.parseFloat(rating)
    const stars = []
    const fullStars = Math.floor(ratingNum)
    const hasHalfStar = ratingNum % 1 >= 0.5

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="h-3 w-3 fill-yellow-400 text-yellow-400" />)
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative">
          <Star className="h-3 w-3 text-yellow-400" />
          <div className="absolute inset-0 overflow-hidden w-[50%]">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
          </div>
        </div>,
      )
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-3 w-3 text-yellow-400" />)
    }

    return stars
  }
  

  return (
    <tr key={product.id} className="hover:bg-gray-50">
      <WarningDialog
        title={"please confirm to delete ?"}
        isOpen={showDeleteWarning}
        onCancel={() => setShowDeleteWarning(false)}
        onConfirm={() => onDelete(product.id)}
      />
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0 mr-3">
            {product.images ? (
              <img src={product.images} alt={product.itemName} className="h-10 w-10 rounded-md object-cover" />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100">
                <span className="text-xs text-gray-500">No img</span>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center">
              <p className="text-sm font-medium text-gray-900">{product.itemName}</p>
              {product.itemType.veg && (
                <span className="ml-2 flex h-4 w-4 items-center justify-center rounded-sm border border-green-500">
                  <span className="h-2 w-2 rounded-full bg-green-500"></span>
                </span>
              )}
              {product.itemType.nonVeg && (
                <span className="ml-2 flex h-4 w-4 items-center justify-center rounded-sm border border-red-500">
                  <span className="h-2 w-2 rounded-full bg-red-500"></span>
                </span>
              )}
            </div>
            <div className="max-w-[120px]">
              <p className="text-xs text-gray-500 line-clamp-1 truncate cursor-context-menu">{product.description || "No description"}</p>
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-col space-y-1">
          <span
            className={`inline-flex justify-center items-center rounded-full px-1.5 py-0.5 text-xs  cursor-pointer font-semibold ${product.status == 'active'
              ? "bg-green-100 text-green-800 border border-green-200"
              : "bg-red-100 text-red-800 border border-red-200"
              }`}
          >
             {product.status === 'active' ? "In Stock" : "Out of Stock"}

             <Switch checked={product.status === 'active'} onChange={() => toggleStock(product)} size="small" />
          </span>

          {product.itemType.chefSpecial && (
            <span className="inline-flex items-center rounded-full bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-800">
              <Award className="mr-0.5 h-3 w-3" />
              Chef's Special
            </span>
          )}

          {product.itemType.bestSeller && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
              <Fire className="mr-0.5 h-3 w-3" />
              Best Seller
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <p className="text-xs text-gray-900">{product.category}</p>
        {product.subCategoryName !== "Na" && <p className="text-xs text-gray-500">Sub: {product.subCategoryName}</p>}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        {product.discountAmount > 0 ? (
          <div>
            <p className="text-xs line-through text-gray-400">₹{product.price}</p>
            <p className="text-sm font-semibold text-green-600">₹{product.price - product.discountAmount}</p>
          </div>
        ) : (
          <p className="text-sm font-semibold">₹{product.price}</p>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <div className="flex justify-end items-center">
          {renderStars(product.rating)}
          <span className="ml-1 text-xs">({product.rating})</span>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-36 rounded-md border border-gray-300 bg-white shadow-md">
              <div className="py-1">
                <button
                  onClick={() => {
                    onEdit(product.id)
                    setDropdownOpen(false)
                  }}
                  className="flex w-full items-center px-4 py-2 text-xs hover:bg-gray-100"
                >
                  <Edit className="mr-2 h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setShowDeleteWarning(true)
                    setDropdownOpen(false)
                  }}
                  className="flex w-full items-center px-4 py-2 text-xs text-red-600 hover:bg-gray-100"
                >
                  <Trash2 className="mr-2 h-3 w-3" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

// Original ProductGrid component
export function ProductGrid({ products, onEdit, onDelete, toggleStock  }) {
  return (
    <SortableContext items={products.map((p) => p?.id?.toString())} strategy={rectSortingStrategy}>
      {
        products.length > 0 ? <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onEdit={onEdit} onDelete={onDelete}  toggleStock={toggleStock} />
          ))}
        </div> :
          <div className="flex justify-center items-center py-20 ">
            <div className="items-center ">
              <PackageSearch className="h-10 w-10 m-auto stroke-gray-400" />
              <p className="text-gray-400">No Products added </p>
            </div>
          </div>
      }

    </SortableContext>
  )
}

// Main component with view toggle
export default function ProductList({ products, onEdit, onDelete, viewMode , toggleStock}) {

  return (
    <div className="space-y-4 max-h-[calc(100vh-184px)] overflow-hidden overflow-y-scroll hide-scrollbar">
      {/* Content based on selected view */}
      {viewMode === "card" ? (
        <ProductGrid products={products} onEdit={onEdit} onDelete={onDelete} toggleStock={toggleStock} />
      ) : (
        <ProductTable products={products} onEdit={onEdit} onDelete={onDelete}  toggleStock={toggleStock}/>
      )}
    </div>
  )
}

